import { request } from '../../api/http';
import { asArray } from '../../api/normalise';
import type { Id } from '../../api/types';

/**
 * RECURRING SCHEDULED WORK — templates, occurrences, and the missed-duty query.
 *
 * ✅ RECONCILED against `tbs-planhub-api` at `eb9d983` — read off `src/routes/recurrence.ts` and
 * `src/services/recurrence.ts`. This file previously described endpoints that did not exist, and
 * FOUR of its inferences were wrong. They are named here so nobody re-derives them:
 *
 *   · templates are PER PROJECT — `GET /projects/{id}/recurrences`. There is no global template list.
 *   · the missed query is GLOBAL — `GET /recurrences/missed`, `projectId` optional, and the API's own
 *     comment says the optionality is the point: a per-project-only version puts the burden back on
 *     somebody remembering to check each project, which is the failure the design replaced.
 *   · occurrences are listed PER TEMPLATE (`/recurrences/{id}/occurrences`). There is no
 *     cross-template list and no from/to window.
 *   · request bodies are camelCase — `intervalN`, `startsOn`, `endsOn`.
 *
 * ⭐⭐ THE ARGUMENT THE WHOLE FEATURE RESTS ON, from db/002 and repeated in the route file:
 *   *"A recurring duty is NOT a ticket with a cadence flag… a period that was MISSED ENTIRELY leaves
 *   no trace. The duty's silence looks exactly like a quiet week… the boardroom ran 14 days with zero
 *   meetings and a HUMAN found it, and three EOD sessions were lost the same way. A DUTY CANNOT
 *   DETECT ITS OWN SILENCE."*
 *
 * ⇒ `GET /recurrences/missed` IS the feature. It is the default view, not a report bolted on.
 */

export type Cadence = 'daily' | 'weekly' | 'monthly' | 'quarterly';

/** ⚠️ A CHECK, not a vocabulary — the generator BRANCHES on it, so it is structure. */
export const CADENCES: readonly Cadence[] = ['daily', 'weekly', 'monthly', 'quarterly'];

export interface Recurrence {
  id: Id;
  project_id: Id;
  title: string;
  description: string | null;
  cadence: Cadence | string;
  interval_n: number;
  assignee: string | null;
  type: string;
  priority: string;
  /** ⚠️ `YYYY-MM-DD`, cast `::text`. ⛔ Never parsed — see `occurrence-model.ts`. */
  starts_on: string;
  /** NULL = runs until paused. ⛔ Not "unknown" — db/002 means unbounded. */
  ends_on: string | null;
  active: boolean;
  created_at: string;
  /** ⭐ Occurrences already written — what tells a reader the template is actually live. */
  occurrence_count: number;
  /**
   * 🔴 THE SERVER'S OWN MISSED COUNT for this template: occurrences past their date with no ticket.
   * ⛔ Not recomputed client-side. It is over ALL of the template's history, whereas anything this
   * client could derive would be over the occurrences it happened to load — and the shorter number
   * would look authoritative.
   */
  missed_count: number;
}

export interface Occurrence {
  id: Id;
  recurrence_id: Id;
  expected_on: string;
  ticket_id: Id | null;
  /** ⭐ `TBS-41`, assembled server-side. NULL exactly when `ticket_id` is. */
  ticket_key: string | null;
  created_at: string;
}

/**
 * One row of `GET /recurrences/missed` — an `Occurrence` that carries its template's identity, so the
 * global view needs no join and no second request.
 */
export interface MissedOccurrence extends Occurrence {
  project_id: Id;
  recurrence_title: string;
  cadence: string;
  /**
   * 🔴 COMPUTED BY POSTGRES: `(COALESCE($1::date, current_date) - o.expected_on)::int`.
   *
   * ⛔ NOT recomputed here, and that is not fastidiousness. "Today" resolved in Node is the browser's
   * timezone; resolved in the database it is the same answer for every reader. A client subtraction
   * would disagree with the server's own `missed` filter for several hours a day, and the row would
   * be listed as missed while claiming it is 0 days late.
   */
  days_late: number;
}

export const MISSED_PATH = '/recurrences/missed';

/**
 * 🔴 THE QUERY THE TABLE EXISTS FOR.
 *
 * ⚠️ `includeInactive` DEFAULTS TO TRUE at the API, deliberately: *"A duty that was paused mid-week
 * still has real periods that were missed before the pause, and hiding them would let 'turn it off'
 * launder a gap."* This client does not send the parameter at all unless someone asks to narrow it,
 * so the default it gets is the API's.
 */
export async function fetchMissedOccurrences(
  query: { projectId?: Id | null } = {},
  signal?: AbortSignal,
): Promise<MissedOccurrence[]> {
  const body = await request<unknown>(MISSED_PATH, {
    signal,
    // ⚠️ `buildUrl` drops null/undefined, so "all projects" sends no parameter — which is the
    // unscoped question, not a scoped one with an empty value.
    query: { projectId: query.projectId ?? null },
  });
  return asArray<MissedOccurrence>(body, MISSED_PATH);
}

export function projectRecurrencesPath(projectId: Id): string {
  return `/projects/${projectId}/recurrences`;
}

export async function fetchRecurrences(projectId: Id, signal?: AbortSignal): Promise<Recurrence[]> {
  const path = projectRecurrencesPath(projectId);
  const body = await request<unknown>(path, { signal });
  return asArray<Recurrence>(body, path);
}

export function occurrencesPath(recurrenceId: Id): string {
  return `/recurrences/${recurrenceId}/occurrences`;
}

/** ⚠️ Ordered `expected_on DESC` by the API. ⛔ Not re-sorted here — see `sortForDisplay`, which is
 *  a DISPLAY ordering applied deliberately and not a correction of the server. */
export async function fetchOccurrences(
  recurrenceId: Id,
  signal?: AbortSignal,
): Promise<Occurrence[]> {
  const path = occurrencesPath(recurrenceId);
  const body = await request<unknown>(path, { signal });
  return asArray<Occurrence>(body, path);
}

/** ⚠️ CAMELCASE, and zod strips unknown keys — `starts_on` would be dropped silently. */
export interface CreateRecurrenceBody {
  title: string;
  description?: string | null;
  cadence: Cadence;
  intervalN?: number;
  assignee?: string | null;
  type?: string;
  priority?: string;
  startsOn: string;
  endsOn?: string | null;
}

export async function createRecurrence(projectId: Id, body: CreateRecurrenceBody): Promise<void> {
  await request<unknown>(projectRecurrencesPath(projectId), { method: 'POST', body });
}

/**
 * ⛔ PAUSE, NEVER DELETE — and the API enforces it rather than trusting this screen: `DELETE
 * /recurrences/{id}` REFUSES once the template has occurrences, because `recurrence_occurrence` is
 * `ON DELETE CASCADE` and dropping it would erase the record of every period it was responsible for,
 * including the missed ones. There is no delete function in this module.
 */
export async function setRecurrenceActive(id: Id, active: boolean): Promise<void> {
  await request<unknown>(`/recurrences/${id}`, { method: 'PATCH', body: { active } });
}

/**
 * ⭐⭐ DOING A MISSED PERIOD LATE, DELIBERATELY — `POST /occurrences/{id}/ticket`.
 *
 * This is why the generator does not back-fill. A ticket manufactured for a period nobody worked
 * would erase the evidence that it was skipped; a human CHOOSING to do it late is a different event,
 * and this is where that choice is recorded. ⭐ The occurrence keeps its original `expected_on`, so
 * the lateness stays visible afterwards — the row moves from "missed" to "done late", not to "fine".
 */
export async function materialiseOccurrence(occurrenceId: Id): Promise<void> {
  await request<unknown>(`/occurrences/${occurrenceId}/ticket`, { method: 'POST' });
}

/**
 * The cron target, exposed as a button: generate the expected periods for every active template in
 * one project. ⭐ Idempotent — running it twice with the same date writes nothing the second time.
 */
export async function generateForProject(projectId: Id): Promise<void> {
  await request<unknown>(`${projectRecurrencesPath(projectId)}/generate`, {
    method: 'POST',
    body: {},
  });
}
